import { LocaleCode } from '@ifixit/i18n/types';
import { FaIcon } from '@ifixit/ui/components/icon';
import { color } from '@ifixit/primitives';
import {
   type CountryCode,
   type LanguageCode,
   ShopifyStorefrontProvider,
} from '@ifixit/shopify-storefront-client';
import { CartDrawerProvider } from '@ifixit/ui-chakra';
import { useContext } from 'react';
import { connect } from 'react-redux';
import { ChakraDefaultProvider } from 'Shared/chakra-provider';
import * as HeaderActions from 'Shared/Header/actions';
import { SmartLink } from 'Shared/smart_link';
import { BreakpointsContext } from 'Shared/utils/breakpoints';
import styled from 'styled-components';
import AuthIcons from './auth_icons';
import { CartDrawerWrapper } from './cart_drawer_wrapper';
import LanguageDropdownContainer from './language_dropdown_container';
import { LocationPicker } from '@ifixit/ui/components/location-picker';
import NotificationsDropdown from './notifications_dropdown';
import UserDropdown from './user_dropdown';
import withNotifications from './with_notifications';
import { getSearchClient } from 'Guide/searchClient';
import { WorkbenchDrawer } from '@ifixit/ui-chakra/header/workbench/workbench-drawer';
import { WorkbenchIcon } from '@ifixit/ui/components/workbench-icon';
import { WORKBENCH_PATH } from '@ifixit/workbench';

const SearchButton = styled.button`
   color: ${color.white};
   display: flex;
   justify-content: center;
   min-width: 40px;
   height: 40px;

   svg {
      transition: color 0.1s;
      width: 24px;
      height: 24px;
   }

   &:hover svg {
      color: ${color.gray[200]};
   }
`;

type TeamType = {
   teamLink: string;
   teamCount: number;
};

export type UserInfoType = {
   username: string;
   uniqueUsername: string;
   profileUrl: string;
   preferencesUrl: string;
   ordersUrl: string;
   reviewsUrl: string;
   notificationsUrl: string;
   avatar: string;
   team: TeamType;
   canIViewManageTab: boolean;
   partner: 'THD' | 'Back Market' | null;
};

const UserLogin = ({ userInfo }: { userInfo: UserInfoType }) =>
   userInfo ? (
      <ConnectedUserDropdown
         username={userInfo.username}
         uniqueUsername={userInfo.uniqueUsername}
         profileUrl={userInfo.profileUrl}
         preferencesUrl={userInfo.preferencesUrl}
         ordersUrl={userInfo.ordersUrl}
         reviewsUrl={userInfo.reviewsUrl}
         notificationsUrl={userInfo.notificationsUrl}
         avatar={userInfo.avatar}
         team={userInfo.team}
         canIViewManageTab={userInfo.canIViewManageTab}
         partner={userInfo.partner}
      />
   ) : (
      <AuthIcons />
   );

const ConnectedNotificationsDropdown = withNotifications(NotificationsDropdown);
const ConnectedUserDropdown = withNotifications(UserDropdown);

const mapDispatchToProps = (dispatch: any) => ({
   toggleMobileSearch: (value: any) => dispatch(HeaderActions.toggleMobileSearch(value)),
});

type DropdownsType = {
   userInfo: any;
   languageDropdown: any;
   shopifyStore: {
      country: CountryCode;
      currencyCode: string;
      language: LanguageCode;
      storeCode: string;
      shopDomain: string;
      storefrontAccessToken: string;
      cartUrl: string;
      storeUrl: string;
   };
   cartDrawer: {
      locale: LocaleCode | null;
   };
   user: {
      priceTiers: Record<string, string> | null;
   };
};

const NavIcons = ({
   dropdowns,
   isOnCart,
   toggleMobileSearch,
   isWorkBenchOpen,
   onWorkbenchOpen,
   onWorkbenchClose,
   showWorkbenchLink,
}: {
   dropdowns: DropdownsType;
   isOnCart: any;
   toggleMobileSearch?: (val: boolean) => void;
   isWorkBenchOpen: boolean;
   onWorkbenchOpen: () => void;
   onWorkbenchClose: () => void;
   showWorkbenchLink: boolean;
}) => {
   const breakpoints = useContext(BreakpointsContext);
   const showMobileSearch = () => {
      if (toggleMobileSearch) {
         toggleMobileSearch(true);
         (document.querySelector('#mobileSearchForm') as HTMLElement).focus();
      }
   };

   const { languageDropdown, userInfo, shopifyStore, cartDrawer } = dropdowns;
   const { languages, translationPreferencesUrl } = languageDropdown;
   const isIfixit = App.siteName === 'ifixit';

   if (breakpoints.isMobileTablet()) {
      return (
         <ShopifyStorefrontProvider {...shopifyStore}>
            <CartDrawerProvider>
               {breakpoints.isMobile() && (
                  <SearchButton aria-label="Search" role="button" onClick={showMobileSearch}>
                     <FaIcon name="magnifying-glass" className="text-white" />
                  </SearchButton>
               )}
               <CartDrawerWrapper
                  locale={cartDrawer.locale}
                  storeCode={shopifyStore.storeCode}
                  linkComponent={SmartLink}
               />
               {!showWorkbenchLink && (
                  <ChakraDefaultProvider>
                     <WorkbenchDrawer
                        isOpen={isWorkBenchOpen}
                        onClose={onWorkbenchClose}
                        storeLocale={App.userPreferredStoreLocale}
                        langid={App.lang}
                        indexPrefix={App.algoliaConfig['indexPrefix']}
                        searchClient={getSearchClient()}
                        workbenchHref={isIfixit ? WORKBENCH_PATH : undefined}
                     />
                  </ChakraDefaultProvider>
               )}
               <UserLogin userInfo={userInfo} />
            </CartDrawerProvider>
         </ShopifyStorefrontProvider>
      );
   }

   return (
      <ShopifyStorefrontProvider {...shopifyStore}>
         <CartDrawerProvider>
            <ChakraDefaultProvider>
               {showWorkbenchLink ? (
                  <WorkbenchIcon href={WORKBENCH_PATH} />
               ) : (
                  <>
                     <WorkbenchIcon onClick={onWorkbenchOpen} />
                     <WorkbenchDrawer
                        isOpen={isWorkBenchOpen}
                        onClose={onWorkbenchClose}
                        storeLocale={App.userPreferredStoreLocale}
                        langid={App.lang}
                        indexPrefix={App.algoliaConfig['indexPrefix']}
                        searchClient={getSearchClient()}
                        workbenchHref={isIfixit ? WORKBENCH_PATH : undefined}
                     />
                  </>
               )}
            </ChakraDefaultProvider>
            {userInfo && (
               <ConnectedNotificationsDropdown notificationsUrl={userInfo.notificationsUrl} />
            )}
            {isIfixit ? (
               <LocationPicker LinkComponent="a" />
            ) : (
               !isOnCart && (
                  <LanguageDropdownContainer
                     languages={languages}
                     translationPreferencesUrl={translationPreferencesUrl}
                  />
               )
            )}
            <CartDrawerWrapper
               locale={cartDrawer.locale}
               storeCode={shopifyStore.storeCode}
               linkComponent={SmartLink}
            />
            <UserLogin userInfo={userInfo} />
         </CartDrawerProvider>
      </ShopifyStorefrontProvider>
   );
};

export default connect(null, mapDispatchToProps)(NavIcons);
